import React from 'react';
import { 
  FileText, 
  Plus, 
  Calendar, 
  User, 
  PoundSterling,
  Download, 
  Edit, 
  Eye, 
  Trash2 
} from 'lucide-react';
import { getStatusBadgeColor, formatCurrency, formatDate } from '../services/reportHelpers';
import authService from '../../../services/authService';

const ReportTable = ({ 
  reports, 
  isLoading, 
  filters,
  onReportSelect, 
  onEditReport, 
  onAddReport,
  onDeleteReport,
  onGeneratePDF 
}) => {
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border">
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading treatment reports...</div>
        </div>
      </div>
    );
  }

  if (reports.length === 0) {
    const hasFilters = filters.hasActiveFilters();
    
    return (
      <div className="bg-white rounded-lg border">
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No treatment reports found</h3>
          <p className="mt-2 text-gray-500">
            {hasFilters
              ? 'Try adjusting your search or filters'
              : 'Get started by creating your first treatment report'
            }
          </p>
          {!hasFilters && (
            <button
              onClick={onAddReport}
              className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto transition-colors"
            >
              <Plus className="h-4 w-4" />
              Create First Report
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border">
      {/* Table Header */}
      <div className="grid grid-cols-12 gap-4 p-4 bg-gray-50 border-b font-medium text-gray-700 text-sm">
        <div className="col-span-2">Report #</div>
        <div className="col-span-2">Customer</div>
        <div className="col-span-2">Service Date</div>
        <div className="col-span-2">Technician</div>
        <div className="col-span-1">Status</div>
        <div className="col-span-1">Amount</div>
        <div className="col-span-2">Actions</div>
      </div>

      {/* Table Body */}
      <div className="divide-y">
        {reports.map((report) => (
          <div
            key={report.id}
            className="grid grid-cols-12 gap-4 p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="col-span-2">
              <div className="font-medium text-blue-600">
                {report.report_number}
              </div>
              <div className="text-sm text-gray-500">
                {formatDate(report.created_at)}
              </div>
            </div>
            
            <div className="col-span-2">
              <div className="font-medium">{report.customer_name}</div>
              {report.customer_phone && (
                <div className="text-sm text-gray-500">{report.customer_phone}</div>
              )}
            </div>
            
            <div className="col-span-2">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4 text-gray-400" />
                {formatDate(report.service_date)}
              </div>
            </div>
            
            <div className="col-span-2">
              <div className="flex items-center gap-1">
                <User className="h-4 w-4 text-gray-400" />
                {report.technician_name}
              </div>
            </div>
            
            <div className="col-span-1">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(report.status)}`}>
                {report.status}
              </span>
            </div>
            
            <div className="col-span-1">
              <div className="flex items-center gap-1">
                <PoundSterling className="h-4 w-4 text-gray-400" />
                {formatCurrency(report.total_amount)}
              </div>
            </div>
            
            <div className="col-span-2 flex items-center gap-2">
              <button
                onClick={() => onReportSelect(report)}
                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                title="View report"
              >
                <Eye className="h-4 w-4" />
              </button>
              
              <button
                onClick={() => onEditReport(report)}
                className="p-1 text-gray-400 hover:text-yellow-600 transition-colors"
                title="Edit report"
              >
                <Edit className="h-4 w-4" />
              </button>
              
              <button
                onClick={() => onGeneratePDF(report.id, report.report_number)}
                className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                title="Generate PDF"
              >
                <Download className="h-4 w-4" />
              </button>
              
              {report.status === 'draft' && (authService.isAdmin() || authService.hasAdminReturn()) && (
                <button
                  onClick={() => onDeleteReport(report.id, report.report_number)}
                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                  title="Delete report"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReportTable;